/**
 * Лоадер приложения: небольшой чертёж квартиры по центру — пока грузятся 3D-сцена и проект.
 * Сначала показывается готовый план, через 2 с запускается цикличная отрисовка штрихом.
 * Тот же чертёж показывает прелоадер в `index.html`, пока не загрузился JS (`blueprintPlan.ts`).
 * Если загрузку можно измерить (модели 3D), под чертежом — тонкая полоса с процентами.
 */
import { useEffect, useState } from "react";
import { BLUEPRINT_VIEWBOX, blueprintPlanMarkup } from "./blueprintPlan";
import { Text } from "./Text";
import "./BlueprintLoader.css";

const HOLD_MS = 2_000;
const CYCLE_MS = 8_000;

/** Чертёж: в первой фазе — готовый, во второй — рисуется штрихом (стили в `BlueprintLoader.css`). */
const PlanSvg = ({ animated }: { animated: boolean }) => (
	<svg
		className={animated ? "bp-plan--anim" : "bp-plan--static"}
		viewBox={BLUEPRINT_VIEWBOX}
		fill="none"
		aria-hidden="true"
		style={{ width: 280 }}
		// Рисунок общий с прелоадером в index.html — одна строка разметки на двоих
		dangerouslySetInnerHTML={{ __html: blueprintPlanMarkup() }}
	/>
);

interface Props {
	/** Сколько процентов загружено; null или нет — загрузку не измерить, полосы нет */
	progress?: number | null;
	/** Что грузится — подпись над полосой */
	progressLabel?: string;
}

export const BlueprintLoader = ({ progress = null, progressLabel = "Загрузка" }: Props) => {
	const [phase, setPhase] = useState<"static" | "anim">("static");
	const [cycle, setCycle] = useState(0);

	useEffect(() => {
		const holdTimer = window.setTimeout(() => setPhase("anim"), HOLD_MS);
		return () => window.clearTimeout(holdTimer);
	}, []);

	useEffect(() => {
		if (phase !== "anim") return;
		const id = window.setInterval(() => setCycle((c) => c + 1), CYCLE_MS);
		return () => window.clearInterval(id);
	}, [phase]);

	return (
		<div className="flex h-full items-center justify-center">
			{/* Колонка: логотип с чертежом, под ними — полоса загрузки ровно под чертежом */}
			<div className="flex flex-col">
				<div className="flex items-center gap-6">
					{/* логотип */}
					<div
						className="flex flex-col leading-none select-none"
						style={{ fontFamily: "Montserrat, sans-serif" }}
					>
						<span className="text-lg font-black tracking-label text-muted">GRID</span>
						<span className="text-base font-light tracking-display text-muted">PLANNER</span>
					</div>
					{/* чертёж */}
					{phase === "static" ? <PlanSvg animated={false} /> : <PlanSvg key={cycle} animated />}
				</div>
				{progress !== null && (
					<div
						className="mt-7 w-70 max-w-loader self-end"
						role="progressbar"
						aria-label={progressLabel}
						aria-valuemin={0}
						aria-valuemax={100}
						aria-valuenow={progress}
					>
						<div className="flex items-baseline justify-between text-xs text-muted">
							<Text as="span" className="tracking-label">
								{progressLabel}
							</Text>
							<Text as="span" className="tabular-nums">
								{progress}%
							</Text>
						</div>
						<div className="mt-1.5 h-0.75 overflow-hidden rounded-full bg-bg">
							<div
								className="h-full rounded-full bg-accent transition-all duration-300 ease-out"
								style={{ width: `${progress}%` }}
							/>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};
